import React from 'react';
import { useSelector } from 'react-redux';
import { formatTime } from 'lib/utils/date';
import moment from 'moment';
import { getMachine } from 'lib/selectors/getMachine';
import { GET_JOBS } from 'lib/api/queries';
import _get from 'lodash/get';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { getIsAPMEnabled } from 'lib/selectors/getIsAPMEnabled';
import { UPDATE_ACTIVITY_SET } from 'lib/api/mutations';
import {
  actionCanonicalActivitySetUpdate,
  actionOptimisticActivitySetUpdate,
} from 'lib/actions';
import Routes from 'lib/utils/routes';
import { throwErrorToast } from 'lib/utils/toast';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { useTranslation } from 'react-i18next';
import {
  WorkOrderCardContainer,
  WorkOrderHeader,
  WorkOrderBody,
  QuantityMade,
  QuantityRequired,
  StatRow,
  Stat,
} from './styled';

const WorkOrderCard = ({ workOrderOp, nextStep, dispatch }) => {
  const {
    sub,
    split,
    lot,
    sequenceNumber,
    scheduledStartDate,
    scheduledFinishDate,
    startQuantity,
    finishQuantity,
    workOrder,
  } = workOrderOp;
  const totalTime =
    moment
      .duration(
        Math.max(
          moment(scheduledFinishDate).diff(scheduledStartDate, 'seconds'),
          0
        )
      )
      .format('h[h] m[m] s[s]', { largest: 2 }) || '0s';

  const machine = useSelector(getMachine);
  const history = useHistory();
  const isApmEnabled = useSelector(getIsAPMEnabled);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const latestWorkOrderId = latestActivitySet?.workOrderId;
  const activitySetRef = latestActivitySet?.activitySetRef;
  const { t } = useTranslation();

  const machineId = _get(machine, 'id');

  const [requestUpdateActivitySet] = useMutation(UPDATE_ACTIVITY_SET, {
    fetchPolicy: 'no-cache',
    update: (
      _cache,
      {
        data: {
          updateActivitySetResponse: { activitySet },
        },
      }
    ) => {
      dispatch(actionOptimisticActivitySetUpdate({ activitySet }));
      // dispatch(actionRefreshRun());
      return history.push({
        pathname: Routes.machineIdHomePath(machine.id),
      });
    },
    onCompleted: ({ updateActivitySetResponse: { activitySet } }) => {
      dispatch(actionCanonicalActivitySetUpdate({ activitySet }));
      // dispatch(actionRefreshRun());
    },
    onError: () => {
      dispatch(
        actionCanonicalActivitySetUpdate({
          activitySet: {
            ...latestActivitySet,
            workOrderId: latestWorkOrderId,
          },
        })
      );
      throwErrorToast(t('Failed to update work order'));
    },
  });

  const onSubmitEditWorkOrderId = (workOrderId) => {
    // shim to nullify workOrderId in db if field is empty string
    requestUpdateActivitySet({
      variables: {
        input: {
          machineRef: machine?.machineRef,
          activitySetRef,
          workOrderId,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateActivitySetResponse: {
          __typename: 'ActivitySet',
          activitySet: {
            ...latestActivitySet,
            workOrderId,
          },
        },
      },
    });
  };

  const [requestJobs] = useLazyQuery(GET_JOBS, {
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      const jobs = _get(res, ['jobs', 'rows'], []);
      const randomJobIndex = Math.floor(Math.random() * jobs.length);
      const job = jobs[randomJobIndex];

      dispatch({
        type: 'SELECT_JOB',
        job,
        workOrderId: workOrder.workOrderId,
      });
      nextStep();
    },
  });

  return (
    <WorkOrderCardContainer
      onClick={() => {
        if (!isApmEnabled) {
          return requestJobs({
            variables: {
              limit: 20,
              machineId,
              filter: '',
            },
          });
        }
        return onSubmitEditWorkOrderId(workOrder.workOrderId);
      }}
    >
      <WorkOrderHeader
        header={workOrder.workOrderId}
        subheader={`${workOrder.partNumber} ${sequenceNumber}`}
      />

      <WorkOrderBody>
        <div>
          <QuantityMade>{startQuantity}</QuantityMade>
          <QuantityRequired>{`/ ${finishQuantity}`}</QuantityRequired>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '5.5rem 5.5rem' }}>
          <Stat
            label="Est. Start"
            value={formatTime(scheduledStartDate)}
            style={{ marginBottom: '.25rem' }}
          />
          <Stat label="WO Due Date" value={formatTime(scheduledFinishDate)} />
          <Stat label="Est. Duration" value={totalTime} />
          <StatRow>
            <Stat label="Lot" value={lot} />
            <Stat label="Split" value={split} />
            <Stat label="Sub" value={sub} />
          </StatRow>
        </div>
      </WorkOrderBody>
    </WorkOrderCardContainer>
  );
};

export default WorkOrderCard;
